/**
 * 路由 - demo login + 三端入口占位
 * 业务页面下个节点实现，这里先让骨架可导航
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { DemoLogin } from './auth/DemoLogin'
import { Layout, type NavItem } from './components/Layout'
import { getIdentity } from './api/client'
import { SellerDashboard } from './pages/seller/Dashboard'
import { SellerPartnerships } from './pages/seller/Partnerships'
import { SellerOrders } from './pages/seller/Orders'
import { SellerFinance } from './pages/seller/Finance'
import { SellerInvoices } from './pages/seller/Invoices'
import { FactoryPartnerships } from './pages/factory/Partnerships'
import { FactoryShipping } from './pages/factory/Shipping'
import { FactoryFinance } from './pages/factory/Finance'
import { FactoryInvoices } from './pages/factory/Invoices'
import { AdminDashboard } from './pages/admin/Dashboard'
import { AdminMerchants } from './pages/admin/Merchants'
import { AdminPartnerships } from './pages/admin/Partnerships'
import { AdminSettlements } from './pages/admin/Settlements'
import { AdminAudit } from './pages/admin/Audit'
import { AdminFee } from './pages/admin/Fee'
import { AdminInvoices } from './pages/admin/Invoices'
import { SellerCompliance } from './pages/seller/Compliance'
import { MerchantApply } from './pages/onboarding/Apply'

/** 登录守卫：未登录跳 /login */
function RequireAuth({ children }: { children: ReactNode }) {
  const loc = useLocation()
  const identity = getIdentity()
  if (!identity) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  }
  return <>{children}</>
}

/** 占位页（下个节点替换为真实业务页） */
function Placeholder({ title, items }: { title: string; items?: string[] }) {
  return (
    <div>
      <h2>{title}</h2>
      <p style={{ color: '#888' }}>本页将在下个节点实现（DB schema → 业务路由 → 前端页面）。</p>
      {items && (
        <ul>
          {items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

const SELLER_NAV: NavItem[] = [
  { label: '工作台', to: '/seller' },
  { label: '代销合作', to: '/seller/partnerships' },
  { label: '订单中心', to: '/seller/orders' },
  { label: '资金账户', to: '/seller/finance' },
  { label: '发票（服务费票）', to: '/seller/invoices' },
  { label: '合规档案', to: '/seller/compliance' },
]

const FACTORY_NAV: NavItem[] = [
  { label: '工作台', to: '/factory' },
  { label: '代销合作', to: '/factory/partnerships' },
  { label: '订单发货', to: '/factory/shipping' },
  { label: '资金账户', to: '/factory/finance' },
  { label: '发票（商品票）', to: '/factory/invoices' },
]

const ADMIN_NAV: NavItem[] = [
  { label: '运营工作台', to: '/admin' },
  { label: '商户管理', to: '/admin/merchants' },
  { label: '合作监控', to: '/admin/partnerships' },
  { label: '分账监控', to: '/admin/settlements' },
  { label: '对账中心', to: '/admin/audit' },
  { label: '抽佣配置', to: '/admin/fee' },
  { label: '双票开票监控', to: '/admin/invoices' },
  { label: '哈希链校验', to: '/admin/chain' },
]

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<DemoLogin />} />
      <Route path="/apply" element={<MerchantApply />} />

      <Route
        path="/seller/*"
        element={
          <RequireAuth>
            <Layout title="商家端" nav={SELLER_NAV}>
              <Routes>
                <Route index element={<SellerDashboard />} />
                <Route path="partnerships" element={<SellerPartnerships />} />
                <Route path="orders" element={<SellerOrders />} />
                <Route path="finance" element={<SellerFinance />} />
                <Route path="invoices" element={<SellerInvoices />} />
                <Route path="compliance" element={<SellerCompliance />} />
              </Routes>
            </Layout>
          </RequireAuth>
        }
      />

      <Route
        path="/factory/*"
        element={
          <RequireAuth>
            <Layout title="工厂端" nav={FACTORY_NAV}>
              <Routes>
                <Route index element={<FactoryShipping />} />
                <Route path="partnerships" element={<FactoryPartnerships />} />
                <Route path="shipping" element={<FactoryShipping />} />
                <Route path="finance" element={<FactoryFinance />} />
                <Route path="invoices" element={<FactoryInvoices />} />
              </Routes>
            </Layout>
          </RequireAuth>
        }
      />

      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <Layout title="运营后台" nav={ADMIN_NAV}>
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="merchants" element={<AdminMerchants />} />
                <Route path="partnerships" element={<AdminPartnerships />} />
                <Route path="settlements" element={<AdminSettlements />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="fee" element={<AdminFee />} />
                <Route path="invoices" element={<AdminInvoices />} />
                <Route path="chain" element={<AdminAudit />} />
              </Routes>
            </Layout>
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}
